import {
  AppearanceType,
  BaseEntityType,
  ContainerEntityType,
  EntityType,
  EntityTypes,
  isContainerEntity,
} from '@email-editor/ui'
import { v4 as uuid } from 'uuid'

export const generateId = () => uuid()

export function getAllDescendants(entities: EntityType[], entityId: string): EntityType[] {
  const entity = getEntity(entities, entityId)
  const initialValue = [] as EntityType[]

  if (!isContainerEntity(entity)) {
    return initialValue
  }

  return entity.childIds.reduce(
    (acc, childId) => [
      ...acc,
      getEntity(entities, childId),
      ...getAllDescendants(entities, childId),
    ],
    initialValue,
  )
}

export function getAllDescendantsIds(entities: EntityType[], entityId: string): string[] {
  const entity = getEntity(entities, entityId)
  const initialValue: string[] = []

  if (!isContainerEntity(entity)) {
    return initialValue
  }

  return entity.childIds.reduce(
    (acc, entityId) => [...acc, entityId, ...getAllDescendantsIds(entities, entityId)],
    initialValue,
  )
}

export function insertChildId(childIds: string[] = [], childId: string, position: number) {
  return [...childIds.slice(0, position), childId, ...childIds.slice(position)]
}

export function copyEntityAndItsDescendants(
  entities: EntityType[],
  entityId: string,
): Record<string, EntityType> {
  const descendants = getAllDescendants(entities, entityId)
  const sourceEntity = getEntity(entities, entityId)

  return [sourceEntity].concat(descendants).reduceRight(
    (acc, descendant) => ({
      ...acc,
      [descendant.id]: {
        ...copyEntity(descendant),
        ...(isContainerEntity(descendant)
          ? {
              childIds: descendant.childIds.map(childId => acc[childId].id),
            }
          : {}),
      },
    }),
    {} as Record<string, EntityType>,
  )
}

export function isVisible(
  appearance: AppearanceType = { mobile: true, desktop: true },
  isDesktop: boolean,
): boolean {
  return isDesktop ? appearance.desktop : appearance.mobile
}

export const getDeviceWarningKey = (
  appearance = { mobile: true, desktop: true },
  isDesktop: boolean,
): 'mobile_only' | 'desktop_only' | undefined => {
  if (!appearance.desktop && !isDesktop) {
    return 'mobile_only'
  }

  if (!appearance.mobile && isDesktop) {
    return 'desktop_only'
  }

  return undefined
}

export function copyEntity(entity: EntityType): EntityType {
  if (isContainerEntity(entity)) {
    return {
      ...entity,
      id: generateId(),
      childIds: [...entity.childIds],
    }
  } else {
    return { ...entity, id: generateId() }
  }
}

export function getEntity(entities: EntityType[], entityId: string): EntityType {
  const entity = entities.find(e => e.id === entityId)
  if (!entity) {
    throw new Error(`Entity not found by id ${entityId}`)
  }
  return entity
}

export function getBodyEntity(entities: EntityType[]): EntityType {
  const entity = entities.find(e => e.type === EntityTypes.Body)
  if (!entity) {
    throw new Error(`Body Entity not found`)
  }
  return entity
}

export function getParentEntity(entities: EntityType[], entityId: string): ContainerEntityType {
  const parentEntity = entities.find(e => isContainerEntity(e) && e.childIds.includes(entityId))
  if (!parentEntity || !isContainerEntity(parentEntity)) {
    throw new Error(`Parent entity not found by childId = ${entityId}`)
  }

  return parentEntity
}

export function updateEntity(entities: EntityType[], updatedEntity: EntityType) {
  return entities.filter(e => e.id !== updatedEntity.id).concat([updatedEntity])
}

export function getEntitiesOutside(entities: EntityType[], entityId: string): EntityType[] {
  const entityIdsToExclude = new Set([entityId, ...getAllDescendantsIds(entities, entityId)])

  return entities.filter(entity => !entityIdsToExclude.has(entity.id))
}
